import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Slider, Stack, Typography } from '@mui/material';
import StyleConstants from '../../../core/styles.constants';
import type { MultiPolygonCoordinates } from '../models/editor-feature';
import { MINIMUM_RING_POINTS, pointCountOf, withPointCount } from '../utils/ring-detail';

interface Props {
  /** The shape's editor id, so the baseline resets when a different shape is selected. */
  readonly featureId: string;
  readonly coordinates: MultiPolygonCoordinates;
  readonly onChange: (coordinates: MultiPolygonCoordinates) => void;
}

/**
 * How many points the selected shape is drawn with.
 *
 * An outline traced from CAD arrives with every arc flattened and every doorway stepped, so
 * arming the Edit points tool on a building border fills the screen with grips too close
 * together to grab. The outline is not wrong — it is drawn at a detail nobody laying out
 * desks needs.
 *
 * Thinning works from the shape as it was when it was selected rather than from what is on
 * screen, which is what makes the slider reversible: points are chosen from the original in
 * order of how little each one changes the outline, so sliding back up returns the corners
 * that were there. Dropping them in place and re-inserting midpoints would not — a midpoint
 * is not the corner that used to be there.
 */
const DetailSlider = ({ featureId, coordinates, onChange }: Props): React.JSX.Element | null => {
  const { t } = useTranslation();

  // The shape as it was before any thinning, plus whatever this slider last produced from it.
  const [baseline, setBaseline] = useState(
    () => ({ featureId, source: coordinates, applied: coordinates }),
  );

  // Adjusting derived state during the render that saw the change, rather than in an effect:
  // an effect would render one frame with a slider describing the previous shape. Anything
  // that is not this slider's own output — a different shape selected, a dragged vertex, an
  // undo — becomes the new baseline, because it is now what the admin means by "the outline".
  if (baseline.featureId !== featureId || (
    coordinates !== baseline.applied && coordinates !== baseline.source
  )) {
    setBaseline({ featureId, source: coordinates, applied: coordinates });
  }

  const maximum = pointCountOf(baseline.source);
  const current = pointCountOf(coordinates);

  // Nothing to thin. A desk outline drawn at nine vertices has no detail to give up, and a
  // slider with a range of two is a control that only looks like one.
  if (maximum <= MINIMUM_RING_POINTS * 2) {
    return null;
  }

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between">
        <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
          {t('floorplan-editor-detail')}
        </Typography>
        <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {t('floorplan-editor-detail-points', { count: current })}
        </Typography>
      </Stack>

      <Slider
        size="small"
        value={Math.min(current, maximum)}
        min={MINIMUM_RING_POINTS}
        max={maximum}
        aria-label={String(t('floorplan-editor-detail'))}
        valueLabelDisplay="auto"
        onChange={(_, value): void => {
          const target = Array.isArray(value) ? value[0] : value;
          const next = withPointCount(baseline.source, target);

          setBaseline({ ...baseline, applied: next });
          onChange(next);
        }}
      />

      <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
        {t('floorplan-editor-detail-hint')}
      </Typography>
    </Stack>
  );
};

export default DetailSlider;
